import { useState, useCallback } from 'react';

export const useQueue = ({ initialValues = [] }) => {
  const [queue, setState] = useState(initialValues);

  /**
   * Enqueue; Adds item to queue.
   */
  const enqueue = useCallback((...items) => {
    setState((current) => [...current, ...items]);
  }, []);

  /**
   * Dequeue; Removes first item in queue.
   */
  const dequeue = useCallback(() => {
    // Immutable solution to remove first element in array.
    setState((current) => current.slice(1));
  }, []);

  /**
   * Update function to allow for more granular control of updating the queue.
   * The `update` function accepts a callback fn where the first arg is the
   * current queue state that we can then manipulate. See tests for examples.
   * */
  const updateQueue = useCallback(
    (fn) => setState((current) => fn([...current])),
    []
  );

  /**
   * Remove all items from queue.
   */
  const clearQueue = useCallback(() => setState(() => []), []);

  /**
   * Returns the first item in the queue.
   */
  const peek = useCallback(() => {
    if (queue.length > 0) {
      return queue[0];
    }

    return undefined;
  }, [queue]);

  return {
    queue,
    length: queue.length,
    enqueue,
    dequeue,
    updateQueue,
    clearQueue,
    peek
  };
};

export default useQueue;
